import { getWidthByHeight } from '../../util/image/pickImageByRatio';
import { useCategoryTreeItem } from '../categoryTree';
import { KuxFilteredCategoriesDTO, tvpil } from '../compat/tvpil';
import type { RhinoImageRatio } from '../image';

export type ImageSize = 'default' | 'compact' | 'medium';

type UseImageSettingsProps = {
  defaultImageRatio?: RhinoImageRatio;
  defaultLandscapeImageHeight?: number;
  defaultPortraitImageHeight?: number;
  imageSize?: ImageSize;
  referenceId: string;
};

type ImageSettings = {
  imageHeight: number | undefined;
  imageRatio: RhinoImageRatio | undefined;
  imageType: number | undefined;
  imageWidth: number | undefined;
};

const imageMap: Record<ImageSize, keyof KuxFilteredCategoriesDTO['imageScreens']> = {
  default: 'imageHeight',
  compact: 'imageHeightCompact',
  medium: 'imageHeightMedium',
};

export function useImageSettings({
  defaultImageRatio,
  defaultLandscapeImageHeight,
  defaultPortraitImageHeight,
  imageSize = 'default',
  referenceId,
}: UseImageSettingsProps): ImageSettings | undefined {
  const category = useCategoryTreeItem(referenceId);

  if (!category) {
    return undefined;
  }

  const imageType = category.railImageProperty?.assetPresentationImageType;
  const imageRatio = tvpil.imageTypes.find((type) => type.id === imageType)?.ratio ?? defaultImageRatio;

  if (!imageRatio) {
    return undefined;
  }

  const imageHeight =
    category.imageScreens[imageMap[imageSize]] ||
    getDefaultImageHeight({ imageRatio, defaultLandscapeImageHeight, defaultPortraitImageHeight });
  const imageWidth = imageHeight ? Math.floor(getWidthByHeight(imageHeight, imageRatio)) : undefined;

  return {
    imageHeight,
    /**
     * @deprecated
     */
    imageRatio,
    imageType,
    imageWidth,
  };
}

type GetDefaultImageHeightProps = {
  defaultLandscapeImageHeight: number | undefined;
  defaultPortraitImageHeight: number | undefined;
  imageRatio: RhinoImageRatio;
};

function getDefaultImageHeight({
  defaultLandscapeImageHeight,
  defaultPortraitImageHeight,
  imageRatio,
}: GetDefaultImageHeightProps) {
  const [widthRatio, heightRatio] = imageRatio.split(/:|x/).map(Number);

  return widthRatio > heightRatio ? defaultLandscapeImageHeight : defaultPortraitImageHeight;
}
